import { useEffect, useState } from 'react';
import {
  getConsentState,
  setConsentState as persistConsentState,
  resetConsent,
  subscribeToConsent,
} from '../utils/consent';

type ConsentBannerProps = {
  showManageButton?: boolean;
};

const ConsentBanner = ({ showManageButton = true }: ConsentBannerProps) => {
  const [consentState, updateConsentState] = useState(getConsentState());
  const [isOpen, setIsOpen] = useState(consentState === 'unknown');

  useEffect(() => {
    return subscribeToConsent((state) => {
      updateConsentState(state);
      if (state === 'unknown') {
        setIsOpen(true);
      }
    });
  }, []);

  const handleAccept = () => {
    persistConsentState('accepted');
    setIsOpen(false);
  };

  const handleDecline = () => {
    persistConsentState('declined');
    setIsOpen(false);
  };

  const handleOpenSettings = () => setIsOpen(true);

  const handleReset = () => {
    resetConsent();
    setIsOpen(true);
  };

  if (!isOpen && consentState !== 'unknown') {
    if (!showManageButton) {
      return null;
    }

    return (
      <button
        type="button"
        onClick={handleOpenSettings}
        className="fixed bottom-6 left-6 z-50 rounded-full border border-white/10 bg-dark/80 px-5 py-2 text-xs font-semibold text-white shadow-lg backdrop-blur transition hover:bg-dark/60"
      >
        Privacy settings
      </button>
    );
  }

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-x-4 bottom-6 z-50 mx-auto max-w-3xl rounded-3xl border border-white/10 bg-dark/95 p-6 text-white shadow-2xl backdrop-blur-lg md:inset-x-auto md:right-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">
            Cookie preferences
          </p>
          <p className="text-sm text-neutral-200">
            We use analytics cookies (Google Analytics & Plausible) to measure usage and conversions.
            Without your consent, only strictly necessary functionality stays active. You can find details in our{' '}
            <a
              className="text-primary underline underline-offset-4 hover:text-primary-light"
              href="/privacy"
            >
              privacy policy
            </a>
            .
          </p>
        </div>
        <div className="flex flex-col gap-2 md:flex-row">
          <button
            type="button"
            onClick={handleDecline}
            className="rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white transition hover:border-primary hover:text-primary"
          >
            Essentials only
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="rounded-full bg-primary px-6 py-2 text-sm font-semibold text-black shadow-lg transition hover:bg-primary-light"
          >
            Accept
          </button>
        </div>
      </div>
      {consentState === 'declined' && (
        <button
          type="button"
          onClick={handleReset}
          className="mt-4 text-xs text-neutral-300 underline underline-offset-4 transition hover:text-white"
        >
          Reset choice
        </button>
      )}
    </div>
  );
};

export default ConsentBanner;


